import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

// Import Components
import Table from '../Table';

// Import icons
import Search from '../../../assets/images/svg/search.svg';

import { dateFormat } from '../../tools/manager';

const AllCouponsInterventions = () => {
  const navigation = useNavigate();

  //  🌱 Store actions
  const getListInterventionOrders = useStoreActions(
    (actions) => actions.middleware.OrdersMiddleware.getListInterventionOrders
  );
  const getUser = useStoreActions(
    (actions) => actions.middleware.AdminMiddleware.getAdmin
  );

  //  🌱 Etats (données + pagination)
  const [interventionCoupons, setInterventionCoupons] = useState([]);   // liste complète chargée (pages cumulées)
  const [listInterventionCoupons, setListInterventionCoupons] = useState([]); // liste filtrée (search)
  const [textSearch, setTextSearch] = useState('');

  const [nextToken, setNextToken] = useState(null);     // token pagination
  const [isLoadingMore, setIsLoadingMore] = useState(false); // état du bouton "Charger plus"

  // 🔍 Filtre de recherche (rejoué quand données ou recherche changent)
  useEffect(() => {
    if (textSearch === '') {
      setListInterventionCoupons(interventionCoupons);
    } else {
      const lower = textSearch.toLowerCase();
      const filtered = interventionCoupons.filter((element) => {
        const companyName = element?.worksite?.company?.name?.toLowerCase() || '';
        const worksiteName = element?.worksite?.name?.toLowerCase() || '';
        const worksiteType = element?.worksite?.type?.name?.toLowerCase() || '';
        const numero = element?.numero || '';
        const username = element?.username?.toLowerCase() || '';
        const date = dateFormat(element?.createdAt);

        return (
          companyName.includes(lower) ||
          worksiteName.includes(lower) ||
          worksiteType.includes(lower) ||
          numero.includes(textSearch) ||
          username.includes(lower) ||
          date.day.includes(textSearch) ||
          date.month.includes(textSearch) ||
          date.year.includes(textSearch)
        );
      });

      setListInterventionCoupons(filtered);
    }
  }, [interventionCoupons, textSearch]);

  //  🌱 Charge une page de bons d'intervention (initial ou "Charger plus")
  const fetchInterventionCoupons = async (token = null) => {
    try {
      if (token) {
        // on est en "load more"
        setIsLoadingMore(true);
      }

      // 🔹 Appel du thunk avec pagination
      const res = await getListInterventionOrders({
        limit: 25,
        nextToken: token,
      });

      // 🔹 Compat : si un jour ça renvoie encore juste un tableau
      const rawItems = Array.isArray(res) ? res : res?.items || [];
      const newToken = Array.isArray(res) ? null : res?.nextToken || null;

      // 🔹 Enrichissement "username" (comme avant)
      const enriched = await Promise.all(
        rawItems.map(async (el) => {
          if (el?.name === 'intervention urgent' && el?.interventionCouponUserId) {
            try {
              const user = await getUser(el.interventionCouponUserId);
              return {
                ...el,
                username: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
              };
            } catch {
              // en cas d'erreur on fallback sur el.name
              return { ...el, username: el?.name || '' };
            }
          }

          return { ...el, username: el?.name || '' };
        })
      );

      // 🔹 Merge avec ce qu'on a déjà si on charge plus
      setInterventionCoupons((prev) =>
        token ? [...prev, ...enriched] : enriched
      );

      // 🔹 Sauvegarde du nouveau nextToken
      setNextToken(newToken);
    } catch (error) {
      console.error(error);
      toast.error(
        "❗️ Une erreur est survenue lors du chargement de la liste des bons d'intervention"
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  //  🌱 Premier chargement
  useEffect(() => {
    fetchInterventionCoupons(null);
  }, []);

  //  🔁 Handler "Charger plus"
  const handleLoadMore = () => {
    if (nextToken && !isLoadingMore) {
      fetchInterventionCoupons(nextToken);
    }
  };

  return (
    <section className="md:pt-8 px-4 md:px-9">
      {/* Barre de recherche */}
      <div className="relative flex mt-6 border border-gray-admin rounded-xl">
        <input
          type="text"
          placeholder="Recherche bons"
          className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
          onInput={(e) => setTextSearch(e.target.value)}
        />

        <img
          src={Search}
          alt="search icon"
          className="absolute left-0 flex items-center w-8 h-5 pl-3 top-4"
        />
      </div>

      {/* Table liste coupons */}
      <div className="mt-9">
        <Table>
          <thead>
            <tr>
              <th>Réalisé le :</th>
              <th>Fait par</th>
              <th>Numéro </th>
              <th>Type de chantier</th>
              <th>Nom du client</th>
              <th className="flex justify-end">Nom du chantier</th>
            </tr>
          </thead>
          <tbody>
            {listInterventionCoupons?.length ? (
              listInterventionCoupons
                // ⚠️ Optionnel : si ton index backend trie déjà par date DESC,
                // tu peux enlever ce sort, mais je le laisse pour garder ton comportement actuel.
                ?.sort((a, b) => b.numero - a.numero)
                ?.map((orderCoupon) => {
                  const dateCouponCreate = dateFormat(orderCoupon.createdAt);
                  return (
                    <tr
                      key={orderCoupon.id}
                      className={`h-16 border-b border-white-admin hover:bg-blue-50 transition-all ${orderCoupon.ctm !== 'true' ? 'cursor-pointer' : ''
                        }`}
                      onClick={() => {
                        if (orderCoupon.ctm !== 'true') {
                          navigation(`/pdf-preview/intervention/${orderCoupon.id}`);
                        }
                      }}
                    >
                      <td>
                        {dateCouponCreate.day}.{dateCouponCreate.month}.
                        {dateCouponCreate.year}
                      </td>
                      <td>{orderCoupon?.username}</td>
                      <td>
                        {orderCoupon?.numero}{' '}
                        {orderCoupon.ctm !== 'true' ? '' : '- pré-rempli'}
                      </td>
                      <td>{orderCoupon?.worksite?.type?.name}</td>
                      <td>{orderCoupon?.worksite?.company?.name}</td>
                      <td className="flex justify-end mt-5">
                        {orderCoupon?.worksite?.name}
                      </td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td
                  className="text-center text-lg font-semibold pt-20"
                  colSpan={6}
                >
                  {textSearch
                    ? "Oups, ce chantier ou client n'existe pas 🤭"
                    : "Ajouter un bon d'intervention"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Bouton "Charger plus" (si pagination disponible) */}
      {nextToken && (
        <div className="flex justify-center mt-6 mb-8">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isLoadingMore ? 'Chargement...' : 'Charger plus'}
          </button>
        </div>
      )}
    </section>
  );
};

export default AllCouponsInterventions;
